// Core Rules p. 33, Armor & Defense. Defense is the armor value plus DEX (capped for mail, ignored for plate) plus a shield's value,
// and reduces a hit when the hero Defends. Costs are in gold pieces.
export type ArmorKind = 'cloth' | 'leather' | 'mail' | 'plate';
export interface ArmorDefinition {
  id: string; name: string; kind: ArmorKind; defense: number;
  /** The most DEX that adds to Defense. Absent means all of it. */
  dexterityCap?: number;
  /** Plate protects by itself: its Defense takes no DEX at all. */
  ignoresDexterity?: boolean;
  /** STR needed to wear it well. The book lists it without a penalty, so it is recorded for display, not enforced. */
  strength?: number;
  cost: number;
}
export interface ShieldDefinition { id: string; name: string; defense: number; strength?: number; cost: number }

const cloth = (id: string, name: string, defense: number, cost: number): ArmorDefinition => ({ id, name, kind: 'cloth', defense, cost });
const leather = (id: string, name: string, defense: number, cost: number, strength?: number): ArmorDefinition => ({ id, name, kind: 'leather', defense, cost, ...strength && { strength } });
const mail = (id: string, name: string, defense: number, cost: number, strength?: number): ArmorDefinition => ({ id, name, kind: 'mail', defense, dexterityCap: 2, cost, ...strength && { strength } });
const plate = (id: string, name: string, defense: number, cost: number, strength: number): ArmorDefinition => ({ id, name, kind: 'plate', defense, ignoresDexterity: true, cost, strength });

export const adventurersGarb = cloth('adventurers-garb', 'Adventurer’s Garb', 2, 10);
export const minorEnchantment = cloth('minor-enchantment', 'Minor Enchantment', 3, 100);
export const majorEnchantment = cloth('major-enchantment', 'Major Enchantment', 4, 1000);
export const epicEnchantment = cloth('epic-enchantment', 'Epic Enchantment', 5, 10000);
/** The Berserker's own dress (Armor: None): furs, hide and bone over bare skin. A game addition with no Defense of its own, so Defense stays DEX. */
export const berserkerFurs = cloth('berserker-furs', 'Berserker Furs', 0, 5);
export const cheapHides = leather('cheap-hides', 'Cheap Hides', 3, 5);
export const oxHide = leather('ox-hide', 'Ox Hide', 4, 45);
export const hardLeather = leather('hard-leather', 'Hard Leather', 5, 300, 1);
export const wyrmhide = leather('wyrmhide', 'Wyrmhide', 6, 2000, 1);
export const rustyMail = mail('rusty-mail', 'Rusty Mail', 6, 15);
export const chainShirt = mail('chain-shirt', 'Chain Shirt', 9, 60, 2);
export const scaleMail = mail('scale-mail', 'Scale Mail', 12, 700, 3);
export const dragonscale = mail('dragonscale', 'Dragonscale', 15, 3000, 4);
export const rustyPlate = plate('rusty-plate', 'Rusty Plate', 10, 25, 2);
export const halfPlate = plate('half-plate', 'Half Plate', 14, 200, 3);
export const fullPlate = plate('full-plate', 'Full Plate', 18, 2000, 4);
export const adamantinePlate = plate('adamantine-plate', 'Adamantine Plate', 22, 5000, 5);

/** Every armor in the book, lightest first. */
export const armors: ArmorDefinition[] = [adventurersGarb, minorEnchantment, majorEnchantment, epicEnchantment, berserkerFurs, cheapHides, oxHide, hardLeather, wyrmhide,
  rustyMail, chainShirt, scaleMail, dragonscale, rustyPlate, halfPlate, fullPlate, adamantinePlate];

export const woodenBuckler: ShieldDefinition = { id: 'wooden-buckler', name: 'Wooden Buckler', defense: 2, cost: 5 };
export const ironShield: ShieldDefinition = { id: 'iron-shield', name: 'Iron Shield', defense: 4, strength: 2, cost: 80 };
export const towerShield: ShieldDefinition = { id: 'tower-shield', name: 'Tower Shield', defense: 6, strength: 3, cost: 1500 };
export const dragonShield: ShieldDefinition = { id: 'dragon-shield', name: 'Dragon Shield', defense: 8, strength: 3, cost: 9000 };
export const shields: ShieldDefinition[] = [woodenBuckler, ironShield, towerShield, dragonShield];

export const armorById = (id: string): ArmorDefinition | undefined => armors.find(armor => armor.id === id);
export const shieldById = (id: string): ShieldDefinition | undefined => shields.find(shield => shield.id === id);

/** The Defense of the armor worn with a shield: unarmored or clothed heroes simply use DEX, which Adventurer's Garb builds on. */
export function armorDefense(armor: ArmorDefinition, dexterity: number, shield?: Pick<ShieldDefinition, 'defense'>): number {
  const bonus = armor.ignoresDexterity ? 0 : Math.min(dexterity, armor.dexterityCap ?? Infinity);
  return armor.defense + bonus + (shield?.defense ?? 0);
}

/**
 * Heroes may wear anything, but a class is trained only in the armor kinds it lists (and `shield`; cloth needs none). Defending in
 * anything else costs one additional action (Core Rules p. 32).
 */
export function armorProficient(training: readonly string[], armor: Pick<ArmorDefinition, 'kind'>, shield?: unknown): boolean {
  // Cloth is everyday dress: nobody needs training to move in it.
  return (armor.kind === 'cloth' || training.includes(armor.kind)) && (!shield || training.includes('shield'));
}
/** Actions a Defend reaction costs: one, plus one while wearing armor or a shield without training. */
export function defendCost(training: readonly string[], armor: Pick<ArmorDefinition, 'kind'>, shield?: unknown): number {
  return armorProficient(training, armor, shield) ? 1 : 2;
}
